import { Injectable, InjectionToken, inject } from '@angular/core';
import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ModulesActions } from './modules.action';
import {
  Module,
  ModuleNameUpdate,
  ModuleRoomUpdate,
  RemoveDataGatewayPayload,
  RemoveModulePayload,
  TransferMeasurementsPayload,
} from './modules.interface';
import * as ModulesSelectors from './modules.selector';

export const MODULES_FACADE = new InjectionToken('MODULES_FACADE');

/**
 * Has to be extended by a specific facade for each project
 * Extending facade must have {providedIn: 'root'} in its Injectable decorator
 */
@Injectable({ providedIn: 'root' })
export class ModulesFacade {
  protected store$ = inject<Store<SettingsState>>(Store);
  protected actions$ = inject(Actions);

  modules$ = this.store$.pipe(select(ModulesSelectors.getModules));
  currentHomeModules$ = this.store$.pipe(select(ModulesSelectors.getCurrentHomeModules));
  currentModule$ = this.store$.pipe(select(ModulesSelectors.getCurrentModule));
  liveAppliances$ = this.store$.select(ModulesSelectors.getLiveAppliances);
  appliances$ = this.store$.select(ModulesSelectors.getGraphsAppliances);
  powerLines$ = this.store$.select(ModulesSelectors.getPowerLines);
  phases$ = this.store$.select(ModulesSelectors.getPhases);

  currentHomeBNMH$ = this.store$.select(ModulesSelectors.currentHomeBNMH);
  currentHomeBtGateway$ = this.store$.select(ModulesSelectors.currentHomeBtGateway);

  getCurrentHomeACModules$ = this.store$.pipe(select(ModulesSelectors.getCurrentHomeACModules));

  getModuleById(moduleId: string) {
    return this.store$.pipe(select(ModulesSelectors.getModuleById(moduleId)));
  }

  getModulesById(ids: string[]) {
    return this.store$.pipe(select(ModulesSelectors.getModulesById(ids)));
  }

  getModulesByType(types: string[]) {
    return this.store$.pipe(select(ModulesSelectors.getModulesByType(types)));
  }

  selectModule(moduleId: string) {
    this.store$.dispatch(ModulesActions.selectModule({ payload: moduleId }));
    return of(true);
  }

  removeDataFromGateway(params: RemoveDataGatewayPayload) {
    this.store$.dispatch(ModulesActions.removeDataGateway({ payload: params }));

    return this.actions$.pipe(
      ofType(ModulesActions.removeDataGatewaySuccess, ModulesActions.removeDataGatewayFailure),
      take(1),
    );
  }

  removeModuleFromHome(params: RemoveModulePayload) {
    this.store$.dispatch(ModulesActions.removeModuleFromHome({ payload: params }));

    return this.actions$.pipe(ofType(ModulesActions.removeModuleFromHomeSuccess), take(1));
  }

  assignModuleToRoom(params: ModuleRoomUpdate, header?: Record<string, string>) {
    this.store$.dispatch(ModulesActions.updateModuleRoom({ payload: params, header }));

    return this.actions$.pipe(
      ofType(ModulesActions.updateModuleRoomSuccess, ModulesActions.updateModuleRoomFailure),
      take(1),
    );
  }

  updateModuleName(params: ModuleNameUpdate, header?: Record<string, string>) {
    this.store$.dispatch(ModulesActions.updateModuleName({ payload: params, header }));

    return this.actions$.pipe(
      ofType(ModulesActions.updateModuleNameSuccess, ModulesActions.updateModuleNameFailure),
      take(1),
    );
  }

  updateModulesStatus(homeId: string, modules: Module[]) {
    this.store$.dispatch(ModulesActions.updateModulesStatus({ payload: { id: homeId, modules } }));
  }

  getCurrentHomeModulesByRoomId(roomId: string): Observable<Module[]> {
    if (cache[roomId]) {
      return cache[roomId];
    } else {
      cache[roomId] = this.store$.pipe(select(ModulesSelectors.getCurrentHomeModulesByRoomId(roomId)));
      return cache[roomId];
    }
  }

  transferModuleMeasurements(payload: TransferMeasurementsPayload) {
    this.store$.dispatch(ModulesActions.transferModuleMeasurements({ payload }));
    return this.actions$.pipe(
      ofType(ModulesActions.transferModuleMeasurementsSuccess, ModulesActions.transferModuleMeasurementsFailure),
      take(1),
    );
  }
}

const cache = {};
